import React from 'react';
import { CustomTooltip } from '../CustomTooltip';
import './PimFloatingBar.css';

/** Every glyph in the PIM floating bar renders at this size — the single source of truth. */
const FLOATING_ICON_SIZE = 32;

interface PimFloatingBarProps {
  children: React.ReactNode;
  className?: string;
  testId?: string;
}

/**
 * Fixed bottom-centre dark pill that holds the PIM floating action buttons.
 * Shared by the templates page and the product-table page so both bars look
 * identical.
 */
export function PimFloatingBar({ children, className, testId }: PimFloatingBarProps) {
  const rootClass = ['pim-floating-bar', className].filter(Boolean).join(' ');
  return (
    <div className={rootClass} data-testid={testId}>
      {children}
    </div>
  );
}

interface PimFloatingButtonProps {
  icon: React.ReactElement;
  title: string;
  ariaLabel: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  loading?: boolean;
  testId?: string;
  /** `danger` gives the destructive red hover (delete); `default` hovers blue. */
  tone?: 'default' | 'danger';
  className?: string;
}

/**
 * A single PIM floating action button. Enforces the 32px icon size and the
 * shared button styling so call sites never restyle or resize the glyph.
 */
export function PimFloatingButton({ icon, title, ariaLabel, onClick, disabled = false, active = false, loading = false, testId, tone = 'default', className: extraClassName }: PimFloatingButtonProps) {
  const className = [
    'pim-floating-bar__btn',
    tone === 'danger' ? 'pim-floating-bar__btn--danger' : '',
    active ? 'pim-floating-bar__btn--active' : '',
    extraClassName,
  ].filter(Boolean).join(' ');
  const sizedIcon = React.cloneElement(icon as React.ReactElement<{ size?: number }>, { size: FLOATING_ICON_SIZE });
  return (
    <CustomTooltip tooltipText={title} placement="top">
      <button type="button" className={className} aria-label={ariaLabel} disabled={disabled} onClick={onClick} data-testid={testId}>
        {loading ? <span className="pim-floating-bar__spinner" /> : sizedIcon}
      </button>
    </CustomTooltip>
  );
}
